'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/lib/ui-kit/tabs';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { PriceAlertEmptyState } from './PriceAlertEmptyState';
import { PriceAlertHistoryView } from './PriceAlertHistoryView';
import { PriceAlertsTable } from './table/PriceAlertsTable';
import type { PriceAlert } from './types';

interface PriceAlertsListViewProps {
  alerts: PriceAlert[];
  isLoading: boolean;
  isError: boolean;
  isUpdating?: boolean;
  onOpenCreate: () => void;
  onEdit: (alert: PriceAlert) => void;
}

export function PriceAlertsListView({
  alerts,
  isLoading,
  isError,
  isUpdating = false,
  onOpenCreate,
  onEdit,
}: PriceAlertsListViewProps) {
  const t = useTranslations('priceAlerts');
  const [tab, setTab] = useState<'active' | 'history'>('active');

  const activeAlerts = alerts.filter((a) => a.status === 'ACTIVE');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as 'active' | 'history')}
        className="min-h-0 flex-1 gap-4"
      >
        <TabsList className="h-auto w-fit justify-start gap-5 p-0">
          <TabsTrigger
            value="active"
            className="flex-none px-0 pb-1.5 pt-1 text-[13px] leading-4 font-[500]"
          >
            {t('tabs.active')}
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="flex-none px-0 pb-1.5 pt-1 text-[13px] leading-4 font-[500]"
          >
            {t('tabs.history')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="size-6 animate-spin text-v2-text-secondary" />
            </div>
          ) : isError ? (
            <PriceAlertEmptyState message={t('list.error')} />
          ) : activeAlerts.length === 0 ? (
            <PriceAlertEmptyState message={t('emptyActive')} />
          ) : (
            <div
              className={cn('transition-opacity', isUpdating && 'pointer-events-none opacity-50')}
              aria-busy={isUpdating || undefined}
            >
              <PriceAlertsTable alerts={activeAlerts} onEdit={onEdit} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="min-h-0 flex-1 overflow-y-auto">
          <PriceAlertHistoryView enabled={tab === 'history'} />
        </TabsContent>
      </Tabs>

      {tab === 'active' && (
        <Button
          appearance="accent"
          size="M"
          className="w-full"
          onClick={onOpenCreate}
          leftIcon={<Icon name="Plus" size={20} />}
        >
          {t('createAlert')}
        </Button>
      )}
    </div>
  );
}
